import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useState,
  type CSSProperties,
  type RefObject,
} from 'react';

/** Gap between the trigger and the floating panel (Figma overlay spacing = 4px). */
const PANEL_GAP = 4;

export type AnchoredPanelAlign = 'start' | 'end';

/**
 * Fixed-position style for a portaled panel anchored to a trigger:
 * - vertically below by default, flipping above when there is not enough room
 *   below and more room above (handles triggers near the viewport bottom);
 * - horizontally aligned to the trigger (`start` = left, `end` = right),
 *   clamped so the panel stays on screen.
 * The trigger width is exposed as the `--spark-dropdown-menu-trigger-width`
 * CSS var so the panel defaults to the trigger width while consumers can still
 * widen it via a panel class.
 *
 * Used by portaled overlay panels (DropdownMenu, AutoComplete) so the list
 * escapes ancestor `overflow` clipping (e.g. inside modals).
 */
export function useAnchoredPanelPosition(
  triggerRef: RefObject<HTMLElement | null>,
  panelRef: RefObject<HTMLElement | null>,
  enabled: boolean,
  align: AnchoredPanelAlign = 'start',
): CSSProperties {
  const [panelStyle, setPanelStyle] = useState<CSSProperties>({});

  const updatePosition = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    const panel = panelRef.current;
    const panelHeight = panel?.offsetHeight ?? 0;
    const panelWidth = panel?.offsetWidth ?? rect.width;
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;

    const spaceBelow = viewportHeight - rect.bottom;
    const spaceAbove = rect.top;
    const openUp = spaceBelow < panelHeight + PANEL_GAP && spaceAbove > spaceBelow;
    const top = openUp
      ? Math.max(PANEL_GAP, rect.top - PANEL_GAP - panelHeight)
      : rect.bottom + PANEL_GAP;

    let left = align === 'end' ? rect.right - panelWidth : rect.left;
    const rightOverflow = left + panelWidth - (viewportWidth - PANEL_GAP);
    if (rightOverflow > 0) left = Math.max(PANEL_GAP, left - rightOverflow);
    if (left < PANEL_GAP) left = PANEL_GAP;

    setPanelStyle({
      top,
      left,
      '--spark-dropdown-menu-trigger-width': `${rect.width}px`,
    } as CSSProperties);
  }, [triggerRef, panelRef, align]);

  useLayoutEffect(() => {
    if (!enabled) return undefined;
    updatePosition();
    // Second pass after the portal paints so measurements are accurate.
    const id = requestAnimationFrame(updatePosition);
    return () => cancelAnimationFrame(id);
  }, [enabled, updatePosition]);

  useEffect(() => {
    if (!enabled) return undefined;
    const handleReposition = (event?: Event) => {
      // Capture-phase scroll also fires for the panel's own list. Repositioning
      // on those events is wasted layout work and can fight an in-panel scrollbar.
      if (
        event?.type === 'scroll' &&
        event.target instanceof Node &&
        panelRef.current?.contains(event.target)
      ) {
        return;
      }
      updatePosition();
    };
    window.addEventListener('scroll', handleReposition, true);
    window.addEventListener('resize', handleReposition);
    return () => {
      window.removeEventListener('scroll', handleReposition, true);
      window.removeEventListener('resize', handleReposition);
    };
  }, [enabled, updatePosition, panelRef]);

  return panelStyle;
}
